import { UploadCloud, ShieldCheck, Banknote, Shield, Lock, CheckCircle, Bot } from "lucide-react";
import { motion, useAnimation, useInView } from "framer-motion";
import { useEffect, useRef } from "react";
import React from "react";

/**
 * CashifyGCMart – 3‑Step Explainer (Enhanced)
 * -------------------------------------------------------
 * • Animated gradient connector with shimmer
 * • Card hover lift + glow
 * • In‑view progress fill animation
 * • Updated copy: manual verification (value + authenticity)
 * • Payout phrasing: preferred method (no specific logos)
 *
 * Requirements:
 *  - TailwindCSS
 *  - framer-motion
 *  - lucide-react
 */

const steps = [
  {
    id: 1,
    title: "Submit Your Card",
    sub: "Enter details or upload images — your choice!",
    color: "from-[#F464A7] via-[#FF6FB2] to-[#FF8CC6]",
    icon: UploadCloud,
    badge: "Step 1",
  },
  {
    id: 2,
    title: "We Verify Manually",
    sub: "Our team reviews and confirms your card's value and authenticity.",
    color: "from-[#8E6BFF] via-[#A387FF] to-[#C9B6FF]",
    icon: ShieldCheck,
    badge: "Step 2",
  },
  {
    id: 3,
    title: "Get Paid Your Way",
    sub: "Receive payment through your preferred method — fast and hassle‑free.",
    color: "from-[#FFC900] via-[#FFD965] to-[#FFE89A]",
    icon: Banknote,
    badge: "Step 3",
  },
];

const container = {
  hidden: { opacity: 0, y: 8 },
  show: {
    opacity: 1,
    y: 0,
    transition: { staggerChildren: 0.18, duration: 0.6, ease: "easeOut" },
  },
};

const item = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

export default function AnimatedProcessSection() {
  return (
    <section className="relative isolate overflow-hidden bg-gradient-to-br from-[#FFE6F4] via-[#FFF4FB] to-[#F7E2FF]">
      {/* Background soft glows - less intense on mobile */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-48 w-48 md:h-72 md:w-72 rounded-full bg-[#FF6FB2]/10 md:bg-[#FF6FB2]/20 blur-2xl md:blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-48 w-48 md:h-72 md:w-72 rounded-full bg-[#8E6BFF]/10 md:bg-[#8E6BFF]/20 blur-2xl md:blur-3xl" />

      <div className="mx-auto max-w-6xl px-4 md:px-6 py-8 md:py-12 lg:py-16">
        <div className="mb-6 md:mb-8 text-center">
          <p className="mx-auto inline-flex items-center rounded-full border border-white/40 bg-white/60 px-2.5 md:px-3 py-0.5 md:py-1 text-[10px] md:text-xs font-medium tracking-wide text-slate-700 backdrop-blur">
            Simple 3‑Step Process
          </p>
          <h2 className="mt-3 md:mt-4 text-2xl md:text-3xl lg:text-4xl font-extrabold leading-tight text-slate-900">
            Submit → Verify → Get Paid
          </h2>
          <p className="mt-1.5 md:mt-2 text-sm md:text-base text-slate-600">Turn your unused gift cards into cash — fast and secure.</p>
        </div>

        <motion.ol
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.35 }}
          className="relative grid grid-cols-1 gap-3 md:gap-6 md:grid-cols-3"
        >
          {/* Animated connector (desktop only) */}
          <div className="absolute left-0 right-0 top-1/2 hidden -translate-y-1/2 md:block" aria-hidden="true">
            <AnimatedConnectorThick />
          </div>

          {steps.map((s, idx) => (
            <motion.li key={s.id} variants={item} className="relative">
              <StepCardAnimated step={s} index={idx} />
            </motion.li>
          ))}
        </motion.ol>

        <div className="mt-6 md:mt-10 flex flex-col md:flex-row flex-wrap items-center justify-center gap-2 md:gap-3">
          <a
            href="/form-submission"
            className="group inline-flex items-center justify-center gap-2 rounded-xl md:rounded-2xl bg-gradient-to-r from-[#FF4C8B] to-[#FF77AC] px-4 md:px-5 py-2.5 md:py-3 text-sm md:text-base text-white shadow-md transition-all hover:from-[#FF77AC] hover:to-[#FF4C8B] hover:shadow-lg w-full md:w-auto"
          >
            <span className="relative font-semibold">
              <span className="absolute inset-0 -z-10 animate-[shine_2.8s_linear_infinite] rounded-xl bg-[linear-gradient(110deg,transparent,rgba(255,255,255,0.35),transparent)]" />
              Sell Your Card Now
            </span>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5 md:h-4 md:w-4 transition group-hover:translate-x-0.5">
              <path d="M5 12h14" />
              <path d="M12 5l7 7-7 7" />
            </svg>
          </a>
          <span className="text-xs md:text-sm text-slate-600 text-center">Manual verification • Same‑day payouts • Up to 93% value</span>
        </div>

        {/* Security Assurance Panel */}
        <SecurityAssurancePanel />

        {/* Trust FAQ Accordion */}
        <TrustFAQAccordion />
      </div>

      {/* Local keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes shine {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(180%); }
        }
      `}</style>
    </section>
  );
}

function StepCardAnimated({ step, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const controls = useAnimation();

  useEffect(() => {
    if (inView) controls.start({ width: "75%" });
  }, [inView, controls]);

  const Icon = step.icon;
  return (
    <div
      ref={ref}
      className="group relative transition-transform duration-300 will-change-transform md:hover:-translate-y-1"
    >
      {/* Glow border - subtle on mobile, more visible on desktop */}
      <div
        className={`absolute -inset-[1px] rounded-2xl md:rounded-3xl bg-gradient-to-r ${step.color} opacity-40 md:opacity-70 blur-sm md:blur-md transition-opacity duration-500 group-hover:opacity-100`}
        aria-hidden="true"
      />

      <div className="relative rounded-2xl md:rounded-3xl border border-white/70 bg-white/95 p-4 md:p-6 shadow-lg md:shadow-[0_10px_40px_rgba(2,6,23,0.06)] backdrop-blur">
        <div className="mb-3 md:mb-5 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 md:gap-2 rounded-full bg-white px-2.5 md:px-3 py-0.5 md:py-1 text-[10px] md:text-xs font-semibold text-slate-700 shadow-sm">
            <span className="inline-block h-2 w-2 md:h-2.5 md:w-2.5 rounded-full bg-pink-300" />
            {step.badge}
          </span>
          <span className="text-[10px] md:text-xs font-medium text-slate-400">Cashifygcmart</span>
        </div>

        <div className="mb-3 md:mb-4 flex items-start gap-2.5 md:gap-3">
          <div className={`rounded-xl md:rounded-2xl bg-gradient-to-r ${step.color} p-2 md:p-3 text-white shadow-md transition-transform duration-300 group-hover:scale-[1.03] flex-shrink-0`}>
            <Icon className="h-5 w-5 md:h-6 md:w-6" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base md:text-lg font-semibold text-slate-900 leading-tight">{step.title}</h3>
            <p className="mt-0.5 md:mt-1 text-xs md:text-sm text-slate-600 leading-snug">{step.sub}</p>
          </div>
        </div>

        {/* Animated progress indicator */}
        <div className="mt-3 md:mt-4 h-1 md:h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
          <motion.div
            initial={{ width: "0%" }}
            animate={controls}
            transition={{ duration: 1.2, ease: "easeOut", delay: 0.2 }}
            className={`h-full rounded-full bg-gradient-to-r ${step.color}`}
          />
        </div>
      </div>

      {/* Arrow connector (mobile) - smaller and less prominent */}
      {index < 2 && (
        <div className="mt-2 md:mt-4 flex items-center justify-center md:hidden">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="h-4 w-4 text-pink-200"
            aria-hidden="true"
          >
            <path d="M12 5v14" />
            <path d="M19 12l-7 7-7-7" />
          </svg>
        </div>
      )}
    </div>
  );
}

function AnimatedConnectorThick() {
  return (
    <div
      className="mx-24 h-2 rounded-full shadow-sm"
      style={{
        backgroundImage:
          "linear-gradient(90deg, rgba(244,100,167,0.4) 0%, rgba(255,111,178,0.7) 25%, rgba(142,107,255,0.7) 50%, rgba(255,201,0,0.7) 75%, rgba(255,232,154,0.4) 100%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 4.5s linear infinite",
      }}
      aria-hidden="true"
    />
  );
}

// Security Assurance Panel Component
function SecurityAssurancePanel() {
  const securityFeatures = [
    {
      icon: Lock,
      title: "256-bit SSL Encryption",
      description: "Bank-level security",
      gradient: "from-blue-500 to-blue-600"
    },
    {
      icon: CheckCircle,
      title: "Verified Vendor Network",
      description: "230+ trusted partners",
      gradient: "from-green-500 to-green-600"
    },
    {
      icon: Bot,
      title: "Fraud Detection AI",
      description: "Real-time protection",
      gradient: "from-purple-500 to-purple-600"
    }
  ];

  return (
    <div className="mt-8 md:mt-12 mx-auto max-w-4xl">
      <div className="text-center mb-4 md:mb-6">
        <h3 className="text-lg md:text-xl font-bold text-slate-900 flex items-center justify-center gap-2">
          <Shield className="h-5 w-5 md:h-6 md:w-6 text-green-600" />
          Your Security is Our Priority
        </h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
        {securityFeatures.map((feature, index) => {
          const IconComponent = feature.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="bg-white/90 backdrop-blur rounded-xl md:rounded-2xl p-4 md:p-5 border border-white/70 shadow-lg hover:shadow-xl transition-all group"
            >
              <div className="text-center">
                <div className={`mx-auto w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-r ${feature.gradient} flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform`}>
                  <IconComponent className="h-6 w-6 md:h-7 md:w-7 text-white" />
                </div>
                <h4 className="text-sm md:text-base font-semibold text-slate-900 mb-1">{feature.title}</h4>
                <p className="text-xs md:text-sm text-slate-600">{feature.description}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// Trust FAQ Accordion Component
function TrustFAQAccordion() {
  const [openIndex, setOpenIndex] = React.useState(null);

  const faqs = [
    {
      question: "How do I know my card is safe?",
      answer: "We use 256-bit SSL encryption (the same security banks use) to protect all your data. Your card information is never stored on our servers - it's transmitted directly to our verified vendor network through secure, encrypted channels. Plus, our AI-powered fraud detection monitors every transaction in real-time."
    },
    {
      question: "When do I get paid?",
      answer: "Most payments are processed within 24 hours of card verification. Once our team confirms your card's validity and value, funds are sent directly to your chosen payment method (PayPal, Zelle, Cash App, Bitcoin, or Chime). Same-day payouts are available for cards verified before 8 PM EST."
    },
    {
      question: "What happens if my card is invalid?",
      answer: "If our verification team finds your card is invalid, expired, or the balance doesn't match, we'll notify you immediately via email. You'll have the opportunity to provide corrected information or submit a different card. No fees are charged for invalid submissions - you only get paid when your card is successfully verified and sold."
    }
  ];

  return (
    <div className="mt-8 md:mt-10 mx-auto max-w-3xl">
      <div className="text-center mb-4 md:mb-6">
        <h3 className="text-lg md:text-xl font-bold text-slate-900">Frequently Asked Questions</h3>
        <p className="text-xs md:text-sm text-slate-600 mt-1">Quick answers to common concerns</p>
      </div>

      <div className="space-y-2 md:space-y-3">
        {faqs.map((faq, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            className="bg-white/90 backdrop-blur rounded-xl md:rounded-2xl border border-white/70 shadow-md overflow-hidden"
          >
            <button
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              className="w-full flex items-center justify-between p-4 md:p-5 text-left hover:bg-white/50 transition-colors"
            >
              <span className="text-sm md:text-base font-semibold text-slate-900 pr-4">{faq.question}</span>
              <svg
                className={`h-5 w-5 md:h-6 md:w-6 text-pink-600 transition-transform flex-shrink-0 ${
                  openIndex === index ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <motion.div
              initial={false}
              animate={{
                height: openIndex === index ? 'auto' : 0,
                opacity: openIndex === index ? 1 : 0
              }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="px-4 md:px-5 pb-4 md:pb-5 pt-0">
                <p className="text-xs md:text-sm text-slate-700 leading-relaxed">{faq.answer}</p>
              </div>
            </motion.div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
